import {useState, useRef, useEffect, useLayoutEffect} from 'react'

interface UseCancellationPolicyPopoverReturn {
  showPopover: boolean
  setShowPopover: (show: boolean) => void
  triggerRef: React.RefObject<HTMLButtonElement>
  popoverRef: React.RefObject<HTMLDivElement>
  popoverPosition: {top: number; left: number}
  updatePopoverPosition: () => void
}

/**
 * Custom hook to manage cancellation policy popover positioning and visibility
 */
export const useCancellationPolicyPopover = (): UseCancellationPolicyPopoverReturn => {
  const [showPopover, setShowPopover] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number
    left: number
  }>({
    top: 0,
    left: 0
  })

  const updatePopoverPosition = () => {
    const trigger = triggerRef.current
    if (!trigger) {
      return
    }
    const rect = trigger.getBoundingClientRect()
    const offset = 8
    const popupWidth = 384 // Tailwind max-w-sm (24rem = 384px)
    const maxLeft = Math.max(offset, window.innerWidth - popupWidth - offset)
    const clampedLeft = Math.min(Math.max(rect.left, offset), maxLeft)
    setPopoverPosition({top: rect.bottom + offset, left: clampedLeft})
  }

  useLayoutEffect(() => {
    if (!showPopover) {
      return
    }
    updatePopoverPosition()
  }, [showPopover])

  useEffect(() => {
    if (!showPopover) {
      return
    }
    const onScrollOrResize = () => updatePopoverPosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setShowPopover(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [showPopover])

  return {
    showPopover,
    setShowPopover,
    triggerRef,
    popoverRef,
    popoverPosition,
    updatePopoverPosition
  }
}
